/**
 * AI-beelden — beeld en video genereren via de Higgsfield API (18 sep 2026).
 * Engine: /api/v1/higgsfield (catalogus met prijsindicatie, dagbudget, generaties in eigen blob).
 * Eerste plek in de flow: moodframes en bewegende storyboard-frames voor pitch en preproductie.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Chip, Stack, MenuItem, Select, FormControl, InputLabel, Switch,
  FormControlLabel, LinearProgress, Alert, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';

const API = `${AI_ENGINE_URL}/api/v1/higgsfield`;
const FONT = '"Inter", system-ui, sans-serif';
const STATUS_LABEL = { queued: 'In wachtrij', in_progress: 'Bezig…', completed: 'Klaar', failed: 'Mislukt', nsfw: 'Geweigerd (inhoud)', canceled: 'Geannuleerd' };

export default function AiBeelden() {
  const [status, setStatus] = useState(null);
  const [model, setModel] = useState('nano_banana');
  const [prompt, setPrompt] = useState('');
  const [inst, setInst] = useState({});
  const [projectNumber, setProjectNumber] = useState('');
  const [referentie, setReferentie] = useState(null); // {url, sasUrl, naam}
  const [uploading, setUploading] = useState(false);
  const [schatting, setSchatting] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [generaties, setGeneraties] = useState(null);
  const [alles, setAlles] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const catalogus = status?.catalogus || [];
  const m = useMemo(() => catalogus.find((c) => c.key === model) || null, [catalogus, model]);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${API}/status`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Status laden mislukt');
        setStatus(d);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  // Alleen resetten als het model écht wisselt — /status herladen na een generatie geeft nieuwe
  // catalogusobjecten en zou anders duur/resolutie/aantal stil terugzetten (Codex 18 sep #17).
  const vorigModel = useRef(null);
  useEffect(() => {
    if (m && vorigModel.current !== m.key) { vorigModel.current = m.key; setInst({ ...m.standaard }); }
  }, [m]);

  useEffect(() => {
    if (!m) return;
    const t = setTimeout(async () => {
      try {
        const r = await authFetch(`${API}/schatting`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ model, instellingen: inst }) });
        if (r.ok) setSchatting(await r.json());
      } catch { /* indicatie */ }
    }, 200);
    return () => clearTimeout(t);
  }, [m, model, inst]);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/generaties?alles=${alles ? 1 : 0}&limit=60`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Generaties laden mislukt');
      setGeneraties(d.generaties || []);
    } catch (e) { setError(e.message); }
  }, [alles]);
  useEffect(() => { laad(); }, [laad]);

  // Poll zolang er iets loopt
  const loopt = (generaties || []).some((g) => g.status === 'queued' || g.status === 'in_progress');
  useEffect(() => {
    if (!loopt) return undefined;
    const t = setInterval(laad, 6000);
    return () => clearInterval(t);
  }, [loopt, laad]);

  const uploadReferentie = async (file) => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData(); fd.append('bestand', file);
      const r = await authFetch(`${API}/referentie`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload mislukt');
      setReferentie({ url: d.url, sasUrl: d.sasUrl, naam: file.name });
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const genereer = async () => {
    if (!m || prompt.trim().length < 3 || bezig) return;
    if (m.referentie_verplicht && !referentie) { toast.error('Dit model heeft een referentiebeeld nodig'); return; }
    setBezig(true); setError(null);
    try {
      const r = await authFetch(`${API}/genereer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model, prompt: prompt.trim(), instellingen: inst, projectNumber: projectNumber.trim() || null,
          referentieUrl: referentie?.url || null, referentieSasUrl: referentie?.sasUrl || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Genereren mislukt');
      setGeneraties((prev) => [d, ...(prev || [])]);
      toast.success(`Gestart · ≈ $${(d.kostenIndicatie ?? 0).toFixed(2)}`);
      const s = await authFetch(`${API}/status`); if (s.ok) setStatus(await s.json());
    } catch (e) { setError(e.message); }
    finally { setBezig(false); }
  };

  const verwijder = async (g) => {
    const ok = await bevestig({ titel: 'Generatie verwijderen?', tekst: 'Het resultaat verdwijnt uit de lijst en uit onze opslag.', knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      const r = await authFetch(`${API}/generaties/${g.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json()).detail || 'Verwijderen mislukt');
      setGeneraties((prev) => (prev || []).filter((x) => x.id !== g.id));
    } catch (e) { toast.error(e.message); }
  };

  const veld = (naam, opties) => {
    if (typeof opties[0] === 'boolean') {
      return (
        <FormControlLabel key={naam} control={<Switch size="small" checked={Boolean(inst[naam])} onChange={(e) => setInst((p) => ({ ...p, [naam]: e.target.checked }))} />}
          label={naam === 'camera_fixed' ? 'Camera stil' : naam} />
      );
    }
    const label = { aspect_ratio: 'Formaat', resolution: 'Resolutie', duration: 'Duur (s)', num_images: 'Aantal', output_format: 'Bestand' }[naam] || naam;
    return (
      <FormControl key={naam} size="small" sx={{ minWidth: 120 }}>
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={inst[naam] ?? ''} onChange={(e) => setInst((p) => ({ ...p, [naam]: e.target.value }))}>
          {opties.map((o) => <MenuItem key={String(o)} value={o}>{String(o)}</MenuItem>)}
        </Select>
      </FormControl>
    );
  };

  const budget = status ? Math.min(100, Math.round(((status.verbruikVandaagUsd || 0) / (status.dagBudgetUsd || 1)) * 100)) : 0;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, sm: 2.5 }, py: 2, fontFamily: FONT }}>
      <PageHeader icon="auto_awesome" title="AI-beelden" subtitle="Moodframes, stills en korte clips via Higgsfield — voor pitch, storyboard en preproductie. Elke generatie kost geld; de indicatie staat bij de knop." />
      {status && !status.configured && <Alert severity="warning" sx={{ mb: 2 }}>Higgsfield is op deze omgeving niet geconfigureerd (HF_KEY ontbreekt).</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {catalogus.map((c) => (
            <Tooltip key={c.key} title={`${c.wanneer} · $${c.prijs} ${c.eenheid}`}>
              <Chip label={c.label} onClick={() => setModel(c.key)} variant={model === c.key ? 'filled' : 'outlined'}
                sx={{ fontWeight: model === c.key ? 700 : 500, bgcolor: model === c.key ? (c.soort === 'video' ? '#ede9fe' : '#dcfce7') : undefined }} />
            </Tooltip>
          ))}
        </Stack>
        {m && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{m.wanneer}</Typography>}
        <TextField fullWidth multiline minRows={3} label="Prompt" placeholder="Beschrijf beeld, licht, camera en sfeer. Nederlands mag; Engels werkt vaak net wat beter." value={prompt} onChange={(e) => setPrompt(e.target.value)} sx={{ mb: 1.5 }} />
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1.5 }}>
          {m && Object.entries(m.velden).map(([naam, opties]) => veld(naam, opties))}
          <TextField size="small" label="Projectnummer (optioneel)" value={projectNumber} onChange={(e) => setProjectNumber(e.target.value)} sx={{ width: 190 }} />
          {m?.referentie && (
            <>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadReferentie(e.target.files?.[0])} />
              <Button size="small" variant="outlined" onClick={() => fileRef.current?.click()} disabled={uploading} sx={{ textTransform: 'none' }}>
                {uploading ? 'Uploaden…' : referentie ? `Referentie: ${referentie.naam}` : `Referentiebeeld${m.referentie_verplicht ? ' (verplicht)' : ''}`}
              </Button>
              {referentie && <Button size="small" onClick={() => setReferentie(null)} sx={{ textTransform: 'none' }}>weg</Button>}
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="contained" disableElevation startIcon={bezig ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />} onClick={genereer}
            disabled={bezig || !status?.configured || prompt.trim().length < 3} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Genereer{schatting ? ` · ≈ $${schatting.kostenIndicatie.toFixed(2)}` : ''}
          </Button>
          {status && (
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary">Dagbudget: ${(status.verbruikVandaagUsd || 0).toFixed(2)} van ${status.dagBudgetUsd} (indicatie)</Typography>
              <LinearProgress variant="determinate" value={budget} sx={{ height: 5, borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: budget > 80 ? '#dc2626' : '#4f46e5' } }} />
            </Box>
          )}
        </Stack>
      </Paper>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={800}>Generaties</Typography>
        <Chip size="small" label={alles ? 'Iedereen' : 'Alleen van mij'} onClick={() => setAlles((v) => !v)} variant="outlined" />
        {loopt && <Typography variant="caption" color="text.secondary">wordt ververst…</Typography>}
      </Stack>
      {generaties === null ? <CircularProgress size={22} /> : generaties.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nog niets gegenereerd.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.5 }}>
          {generaties.map((g) => (
            <Paper key={g.id} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ aspectRatio: '16 / 9', bgcolor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {g.status === 'completed' && g.resultaatUrl ? (
                  g.soort === 'video'
                    ? <video src={g.resultaatUrl} controls preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <img src={g.resultaatUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <Stack alignItems="center" spacing={1}>
                    {(g.status === 'queued' || g.status === 'in_progress') && <CircularProgress size={22} sx={{ color: '#a5b4fc' }} />}
                    <Typography sx={{ color: '#cbd5e1', fontSize: 13 }}>{STATUS_LABEL[g.status] || g.status}</Typography>
                    {g.fout && <Typography sx={{ color: '#fca5a5', fontSize: 11, px: 2, textAlign: 'center' }}>{g.fout}</Typography>}
                  </Stack>
                )}
              </Box>
              {(g.resultaatUrls || []).length > 1 && (
                <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, bgcolor: '#0f172a' }}>
                  {g.resultaatUrls.map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noopener noreferrer" style={{ flex: 1, minWidth: 0 }}>
                      <img src={u} alt="" style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                    </a>
                  ))}
                </Box>
              )}
              <Box sx={{ p: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{g.prompt}</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={(catalogus.find((c) => c.key === g.model) || {}).label || g.model} sx={{ height: 20, fontSize: 10.5 }} />
                  {g.projectNumber && <Chip size="small" label={g.projectNumber} sx={{ height: 20, fontSize: 10.5, bgcolor: '#eef2ff', color: '#3730a3' }} />}
                  <Typography variant="caption" color="text.secondary">≈ ${(g.kostenIndicatie ?? 0).toFixed(2)} · {String(g.createdAt || '').slice(0, 16).replace('T', ' ')}</Typography>
                  <Box sx={{ flex: 1 }} />
                  {g.resultaatUrl && <IconButton size="small" href={g.resultaatUrl} target="_blank" rel="noopener"><OpenInNewIcon sx={{ fontSize: 16 }} /></IconButton>}
                  <IconButton size="small" onClick={() => verwijder(g)}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
                </Stack>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
        Prijzen zijn een indicatie op basis van de Higgsfield-tarieven; de echte afschrijving staat in de Higgsfield-console. Resultaten worden in onze eigen opslag bewaard (de leverancier houdt ze 7 dagen).
      </Typography>
    </Box>
  );
}
